import { DateTime } from 'luxon';
import { payCalculator } from '@shiftly/shared/src/calculators/payRouter';
import { ShiftData } from '@shiftly/shared/src/calculators/common';
import { NO_PAY_ROLE, TRIAL_ROLE } from '@shiftly/shared/src/constants';
import { Shift, Model, User } from '@shiftly/shared/src/schema';
import { formatCurrencyGBP } from '../../../../utils/format';
import { UserPayData } from '../../../../lib/supabase/pay/supabasePayService';
import SupabaseModelContractService from '../../../../lib/supabase/models/supabaseModelContractService';

export interface ContractSplit {
  split: number;
  effective_date: string;
  pay_for_period: number;
}

export interface ModelPaySummary {
  model_id: string;
  model_name: string;
  total_pay: number;
  set: number;
  contracted_splits: ContractSplit[];
  vat: boolean;
}

export interface SetGroup {
  set: number;
  label: string;
  data: ModelPaySummary[];
}

export interface ModelPaySummarySpreadsheetRow {
  A: string; // SPLIT %
  B: string; // VAT (checkbox/boolean)
  C: string; // NAME
  D: string; // PRE-SPLIT TOTAL
  E: string; // SPLIT
  F: string; // VAT Amount
  G: string; // TOTAL
}

export interface PayCalculationData {
  user_id: string;
  user_name: string;
  calculated_total: number;
  stored_total: number;
  difference: number;
  stored_pay_id?: number;
}

export interface PayData {
  [user_id: string]: ShiftData[];
}

export const calculateUserPayData = async (
  shifts: Shift[],
  users: User[],
  selected_month: DateTime,
  exchange_rate: number | null
): Promise<PayData> => {
  const pay_data: PayData = {};

  // Group shifts by user
  const shifts_by_user = shifts.reduce((acc, shift) => {
    if (!shift.user_id) return acc;
    if (!acc[shift.user_id]) {
      acc[shift.user_id] = [];
    }
    acc[shift.user_id].push(shift);
    return acc;
  }, {} as Record<string, Shift[]>);

  // Calculate pay for each user
  for (const [user_id, user_shifts] of Object.entries(shifts_by_user)) {
    const user = users.find(u => u.id === user_id);
    if (!user?.pay_class) continue;

    // Skip users with the excluded roles
    if (user.roles.includes(NO_PAY_ROLE) || user.roles.includes(TRIAL_ROLE)) {
      continue;
    }

    try {
      const result = await payCalculator(
        user.tax?.country || '',
        user_shifts,
        selected_month.month,
        selected_month.year,
        user.pay_class as 'UK' | 'FP',
        exchange_rate
      );

      if (result) {
        pay_data[user_id] = result;
      }
    } catch (error) {
      console.error(`Error calculating pay for user ${user_id}:`, error);
    }
  }

  return pay_data;
};

export const calculateModelSummaries = async (
  pay_data: PayData,
  models: Model[],
  selected_month: DateTime
): Promise<SetGroup[]> => {
  const summaries: Record<string, ModelPaySummary> = {};
  const model_contracts_cache: Record<string, any[]> = {};

  // Get unique model IDs from pay data
  const model_ids = new Set<string>();
  Object.entries(pay_data).forEach(([_, user_shift_data]) => {
    user_shift_data.forEach(shift_data => {
      model_ids.add(shift_data.model_id);
    });
  });

  // Fetch contracts for all models
  for (const model_id of model_ids) {
    try {
      const contracts = await SupabaseModelContractService.getAllModelContracts(model_id);
      model_contracts_cache[model_id] = contracts;
    } catch (error) {
      console.error(`Error fetching contracts for model ${model_id}:`, error);
      model_contracts_cache[model_id] = [];
    }
  }

  // Calculate month boundaries
  const month_start = selected_month.startOf('month').set({ hour: 6, minute: 0, second: 0, millisecond: 0 });
  const month_end = selected_month.plus({ months: 1 }).startOf('month').set({ hour: 5, minute: 59, second: 59, millisecond: 999 });

  // Process each model's pay data
  Object.entries(pay_data).forEach(([_, user_shift_data]) => {
    user_shift_data.forEach(shift_data => {
      const { model_id, pay_due, date } = shift_data;

      const model = models?.find(m => m.id === model_id);
      if (model?.account_type !== 'Normal' && model?.account_type !== 'Unlock') {
        return;
      }

      if (!summaries[model_id]) {
        const contracts = model_contracts_cache[model_id] || [];

        // Filter contracts active during the month and sort by effective_date (ascending)
        const active_contracts = contracts
          .filter(c => {
            const effective_date = DateTime.fromISO(c.effective_date, { zone: 'UTC' });
            return effective_date <= month_end;
          })
          .sort((a, b) => DateTime.fromISO(a.effective_date, { zone: 'UTC' }).toMillis() - DateTime.fromISO(b.effective_date, { zone: 'UTC' }).toMillis());

        // Get the most recent contract for VAT
        const most_recent_contract = active_contracts.length > 0
          ? active_contracts.reduce((latest, current) =>
              DateTime.fromISO(current.effective_date, { zone: 'UTC' }) > DateTime.fromISO(latest.effective_date, { zone: 'UTC' }) ? current : latest
            )
          : null;

        // Initialize contract splits array
        const contracted_splits: ContractSplit[] = active_contracts.map(contract => ({
          split: contract.contracted_split,
          effective_date: contract.effective_date,
          pay_for_period: 0
        }));

        summaries[model_id] = {
          model_id: model_id,
          model_name: model?.name || 'Unknown Model',
          total_pay: 0,
          set: model?.set || 0,
          contracted_splits: contracted_splits.length > 0 ? contracted_splits : [
            {
              split: 0,
              effective_date: month_start.toFormat('yyyy-MM-dd'),
              pay_for_period: 0
            }
          ],
          vat: most_recent_contract?.vat || false
        };
      }

      // Add pay to total
      summaries[model_id].total_pay += pay_due;

      // Determine which contract period this shift belongs to
      const shift_date = date;
      const summary = summaries[model_id];

      // Find the applicable contract for this shift (effective_date is INCLUSIVE)
      let applicable_split_index = -1;
      for (let i = summary.contracted_splits.length - 1; i >= 0; i--) {
        const contract_date = DateTime.fromISO(summary.contracted_splits[i].effective_date, { zone: 'UTC' });
        if (shift_date >= contract_date) {
          applicable_split_index = i;
          break;
        }
      }

      // Add pay to the appropriate contract period
      if (applicable_split_index >= 0) {
        summary.contracted_splits[applicable_split_index].pay_for_period += pay_due;
      } else if (summary.contracted_splits.length > 0) {
        // If no contract found, add to the first contract (fallback)
        summary.contracted_splits[0].pay_for_period += pay_due;
      }
    });
  });

  // Group by set
  const groups: Record<number, SetGroup> = {};
  Object.values(summaries).forEach(model => {
    if (!groups[model.set]) {
      groups[model.set] = {
        set: model.set,
        label: `Set ${model.set}`,
        data: []
      };
    }
    groups[model.set].data.push(model);
  });

  // Sort models within each set by name
  Object.values(groups).forEach(group => {
    group.data.sort((a, b) => a.model_name.localeCompare(b.model_name));
  });

  // Return groups sorted by set number
  return Object.values(groups).sort((a, b) => a.set - b.set);
};

export const getSpreadsheetData = (groupData: ModelPaySummary[]): ModelPaySummarySpreadsheetRow[] => {
  const rows: ModelPaySummarySpreadsheetRow[] = [];

  groupData.forEach(model => {
    const has_vat = model.vat;

    // Calculate totals across all contract periods
    let total_split_amount = 0;
    let total_vat_amount = 0;

    model.contracted_splits.forEach(contract_split => {
      const split_amount = (contract_split.pay_for_period * contract_split.split) / 100;
      const vat_amount = has_vat ? split_amount * 0.2 : 0;
      total_split_amount += split_amount;
      total_vat_amount += vat_amount;
    });

    const final_total = total_split_amount + total_vat_amount;

    // Format split percentages - show all unique splits
    const unique_splits = [...new Set(model.contracted_splits.map(cs => cs.split))];
    const split_display = unique_splits.length === 1
      ? `${unique_splits[0]}%`
      : unique_splits.map(s => `${s}%`).join(', ');

    rows.push({
      A: split_display,
      B: has_vat ? '✓' : '✗',
      C: model.model_name,
      D: formatCurrencyGBP(model.total_pay),
      E: formatCurrencyGBP(total_split_amount),
      F: formatCurrencyGBP(total_vat_amount),
      G: formatCurrencyGBP(final_total)
    });
  });

  // Calculate grand totals
  const grand_totals = groupData.reduce((totals, model) => {
    const has_vat = model.vat;

    let model_split_amount = 0;
    let model_vat_amount = 0;

    model.contracted_splits.forEach(contract_split => {
      const split_amount = (contract_split.pay_for_period * contract_split.split) / 100;
      const vat_amount = has_vat ? split_amount * 0.2 : 0;
      model_split_amount += split_amount;
      model_vat_amount += vat_amount;
    });

    const model_final_total = model_split_amount + model_vat_amount;

    return {
      pre_split_total: totals.pre_split_total + model.total_pay,
      split_amount: totals.split_amount + model_split_amount,
      vat_amount: totals.vat_amount + model_vat_amount,
      final_total: totals.final_total + model_final_total
    };
  }, { pre_split_total: 0, split_amount: 0, vat_amount: 0, final_total: 0 });

  rows.push({
    A: '',
    B: '',
    C: '',
    D: '',
    E: '',
    F: '',
    G: ''
  });

  rows.push({
    A: '',
    B: '',
    C: 'TOTAL',
    D: formatCurrencyGBP(grand_totals.pre_split_total),
    E: formatCurrencyGBP(grand_totals.split_amount),
    F: formatCurrencyGBP(grand_totals.vat_amount),
    G: formatCurrencyGBP(grand_totals.final_total)
  });

  return rows;
};

export const calculatePayDiscrepancies = (
  pay_data: PayData,
  stored_pays: UserPayData[],
  users: User[]
): PayCalculationData[] => {
  if (!pay_data || !stored_pays || !users || Object.keys(pay_data).length === 0) {
    return [];
  }

  const discrepancies: PayCalculationData[] = [];

  // For each user with calculated pay data
  Object.entries(pay_data).forEach(([user_id, user_shift_data]) => {
    const user = users.find(u => u.id === user_id);
    if (!user) return;

    // Skip users with excluded roles
    if (user.roles.includes(NO_PAY_ROLE) || user.roles.includes(TRIAL_ROLE)) {
      return;
    }

    // Calculate total sales pay from current calculation
    const calculated_total = user_shift_data.reduce((sum, shiftData) => sum + shiftData.pay_due, 0);

    // Find stored pay document for this user
    const stored_pay = stored_pays.find(pay => pay.invoice?.user_id === user_id);

    if (stored_pay) {
      // Get stored sales total from breakdown
      const stored_total = stored_pay.breakdown?.sales?.total || 0;
      const difference = Math.abs(calculated_total - stored_total);

      // Only log if there's a meaningful difference (more than £0.01 to account for rounding)
      if (difference > 0.01) {
        discrepancies.push({
          user_id,
          user_name: user.full_name || user.email || user_id,
          calculated_total: calculated_total,
          stored_total: stored_total,
          difference,
          stored_pay_id: stored_pay.invoice?.id
        });
      }
    } else {
      // User has calculated pay but no stored pay document
      discrepancies.push({
        user_id,
        user_name: user.full_name || user.email || user_id,
        calculated_total: calculated_total,
        stored_total: 0,
        difference: calculated_total
      });
    }
  });

  return discrepancies;
};

export const logPayDiscrepancies = (
  discrepancies: PayCalculationData[],
  month_number: number,
  year_number: number
): void => {
  if (discrepancies.length > 0) {
    console.group(`🔍 Sales Pay Calculation Discrepancies for ${month_number + 1}/${year_number}`);
    console.log(`Found ${discrepancies.length} users with discrepancies:`);

    discrepancies.forEach(discrepancy => {
      console.log(`\n👤 ${discrepancy.user_name} (${discrepancy.user_id}):`);
      console.log(`   Calculated Sales: £${discrepancy.calculated_total.toFixed(2)}`);
      console.log(`   Stored Sales: £${discrepancy.stored_total.toFixed(2)}`);
      console.log(`   Difference: £${discrepancy.difference.toFixed(2)}`);
      if (discrepancy.stored_pay_id) {
        console.log(`   Stored Pay ID: ${discrepancy.stored_pay_id}`);
      }
    });

    const total_calculated = discrepancies.reduce((sum, d) => sum + d.calculated_total, 0);
    const total_stored = discrepancies.reduce((sum, d) => sum + d.stored_total, 0);
    const total_difference = Math.abs(total_calculated - total_stored);

    console.log(`\n📊 Summary:`);
    console.log(`   Total Calculated Sales: £${total_calculated.toFixed(2)}`);
    console.log(`   Total Stored Sales: £${total_stored.toFixed(2)}`);
    console.log(`   Total Difference: £${total_difference.toFixed(2)}`);
    console.groupEnd();
  } else {
    console.log(`✅ No sales pay calculation discrepancies found for ${month_number + 1}/${year_number}`);
  }
};